// Google Analytics 4 + Google Ads - Serviço centralizado de eventos
// Todos os eventos GA são disparados por este service para manter consistência.
// Tags só são carregadas após consentimento de cookies (LGPD).

import { isWebView, getWebViewApp } from '../lib/webview';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA4_ID: string = 'G-9JZFL4PS0D';
const GADS_ID: string = 'AW-17447922086';
const CONSENT_KEY: string = 'meugantt_cookie_consent';

// ===================== GOOGLE ADS — AÇÕES DE CONVERSÃO =====================
// O ID da conta (AW-17447922086) já está certo. Falta apenas o "label" de cada
// ação de conversão. Crie as ações em Google Ads > Objetivos > Conversões,
// ative "Enhanced conversions for web", e COLE OS LABELS aqui (algo como 'aBcD1eFgH2'):
const GADS_LABEL_CADASTRO: string = 'LABEL_CADASTRO';     // <-- COLE o label da conversão "Cadastro"
const GADS_LABEL_ASSINATURA: string = 'LABEL_ASSINATURA'; // <-- COLE o label da conversão "Assinatura paga"

let ga4Initialized: boolean = false;

// Fila de eventos disparados ANTES do consentimento de cookies (o gtag ainda não
// existe). São reproduzidos quando o GA4 inicializa. Nada sai antes do aceite; se
// o usuário rejeitar cookies, a fila nunca é reproduzida (LGPD). Resolve o sumiço
// de sign_up/begin_checkout, que acontecem no cadastro, antes do aceite.
const MAX_PENDING = 50;
let pending: Array<{ name: string; params: Record<string, unknown> }> = [];
// Último user_id/propriedades, reaplicado no init para o User-ID sobreviver ao
// timing do consentimento (login antes do aceite de cookies).
let lastUser: { userId: string; properties: Record<string, unknown> } | null = null;

// Tracking só roda em produção real. Nunca em dev/localhost (regra do projeto).
function isTrackingEnabled(): boolean {
  if (import.meta.env.DEV) return false;
  const host = window.location.hostname;
  return host !== 'localhost' && host !== '127.0.0.1' && host !== '0.0.0.0';
}

// Inicializa GA4 + Google Ads dinamicamente (só após consentimento e fora de dev)
export function initializeGA4(): void {
  if (ga4Initialized) return;
  if (!isTrackingEnabled()) return;
  ga4Initialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA4_ID);
  window.gtag('config', GADS_ID);

  // Dimensão de coorte para a sessão inteira: cobre eventos disparados quando o
  // usuário ainda está deslogado (ex.: auth_error antes de qualquer login).
  window.gtag('set', 'user_properties', webviewParams());

  // Reaplica o User-ID/propriedades conhecidos (se o login veio antes do aceite).
  if (lastUser) {
    window.gtag('set', 'user_id', lastUser.userId);
    window.gtag('set', 'user_properties', lastUser.properties);
  }
  // Reproduz os eventos enfileirados antes do consentimento.
  for (const e of pending) window.gtag('event', e.name, e.params);
  pending = [];
}

// Verifica consentimento salvo e inicializa GA4 + Google Ads se já aceito
export function checkAndInitGA4(): void {
  const consent = localStorage.getItem(CONSENT_KEY);
  if (consent === 'accepted') {
    initializeGA4();
  }
}

// Dispara um evento GA4. Enfileira no dataLayer (o stub do gtag empurra mesmo
// antes do script carregar). Barra em dev/localhost.
function trackEvent(eventName: string, params: Record<string, unknown> = {}): void {
  if (!isTrackingEnabled()) return;
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  } else if (pending.length < MAX_PENDING) {
    // Antes do consentimento: enfileira para reproduzir quando o GA4 inicializar.
    pending.push({ name: eventName, params });
  }
}

// Dispara uma conversão do Google Ads. Ignora silenciosamente se o label ainda
// é o placeholder (evita registrar conversões falsas antes de você configurar).
function sendAdsConversion(label: string, params: Record<string, unknown> = {}): void {
  if (!isTrackingEnabled()) return;
  if (typeof window.gtag !== 'function') return;
  if (!label || label.startsWith('LABEL_')) return; // placeholder não configurado
  window.gtag('event', 'conversion', { send_to: `${GADS_ID}/${label}`, ...params });
}

// SHA-256 em hex (Web Crypto). Requer secure context (https) — ok em produção.
async function sha256Hex(value: string): Promise<string> {
  const data = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// Enhanced conversions: envia o email do usuário hasheado (SHA-256, normalizado)
// para o Google casar a conversão com a conta de quem clicou no anúncio.
async function setEnhancedUserData(email?: string): Promise<void> {
  if (!isTrackingEnabled() || typeof window.gtag !== 'function' || !email) return;
  try {
    const sha256_email_address = await sha256Hex(email.trim().toLowerCase());
    window.gtag('set', 'user_data', { sha256_email_address });
  } catch {
    /* crypto.subtle indisponível */
  }
}

// Parâmetros de coorte WebView, mesclados nos eventos de auth. webview_app só
// entra quando identificado (evita poluir o GA4 com chave vazia).
function webviewParams(): Record<string, unknown> {
  const app = getWebViewApp();
  return app ? { is_webview: isWebView(), webview_app: app } : { is_webview: isWebView() };
}

const analytics = {
  // ==================== AUTH ====================
  // Cadastro concluído. Dispara sign_up (GA4) + conversão de cadastro (Google Ads).
  // O email (opcional) alimenta enhanced conversions. Fire-and-forget nos callers.
  async trackSignUp(method: string = 'email', email?: string): Promise<void> {
    await setEnhancedUserData(email);
    trackEvent('sign_up', { method, ...webviewParams() });
    sendAdsConversion(GADS_LABEL_CADASTRO);
  },

  trackLogin(method: string = 'email'): void {
    trackEvent('login', { method, ...webviewParams() });
  },

  // ==================== CONVERSÃO / SUBSCRIPTION ====================
  trackPlanView(planName: string, billingCycle: string): void {
    trackEvent('view_item', {
      item_name: planName,
      billing_cycle: billingCycle,
    });
  },

  // value em REAIS (não centavos) — GA4 espera o valor na unidade da moeda.
  trackCheckoutInitiated(planName: string, billingCycle: string, value: number): void {
    // Guarda o plano em aberto para o checkout_abandoned (?checkout=canceled).
    try {
      localStorage.setItem('meugantt_last_checkout', JSON.stringify({ plan: planName, billing_cycle: billingCycle }));
    } catch { /* localStorage indisponível */ }
    trackEvent('begin_checkout', {
      item_name: planName,
      billing_cycle: billingCycle,
      value,
      currency: 'BRL',
    });
  },

  // Compra confirmada (reforço client-side; a fonte autoritativa é o webhook via
  // Measurement Protocol). value em REAIS, transactionId = id da subscription do
  // Stripe para o GA4 deduplicar com o evento server-side. Também dispara a
  // conversão de assinatura paga no Google Ads (enhanced via email).
  async trackPurchase(value: number, currency: string, transactionId: string, planName: string, email?: string): Promise<void> {
    await setEnhancedUserData(email);
    trackEvent('purchase', {
      transaction_id: transactionId,
      value,
      currency,
      items: [{ item_name: planName, price: value, quantity: 1 }],
    });
    sendAdsConversion(GADS_LABEL_ASSINATURA, { value, currency, transaction_id: transactionId });
  },

  trackPlanUpgradeClick(source: string): void {
    trackEvent('plan_upgrade_click', { source });
  },

  trackManageSubscription(): void {
    trackEvent('manage_subscription');
  },

  // ==================== PROJETOS ====================
  // project_created é o evento canônico de "projeto criado", disparado em TODO
  // caminho de criação. creation_method: 'manual' | 'template' | 'ai' | 'import'.
  trackProjectCreated(projectName: string, method: string = 'manual'): void {
    trackEvent('project_created', { project_name: projectName, creation_method: method });
  },

  // Projeto criado por IA / modelo da galeria (onboarding). Emite o canônico
  // project_created com o método — antes disparava ai_project_created e por isso
  // o project_created tinha "sumido" do GA4 quando o onboarding passou a usar templates.
  trackAIProjectCreated(projectName: string, source: 'ai' | 'template' = 'ai'): void {
    trackEvent('project_created', { project_name: projectName, creation_method: source });
  },

  trackProjectDeleted(): void {
    trackEvent('project_deleted');
  },

  // Pastas de projetos. Sem nome da pasta no evento: nome de pasta é nome de
  // cliente do usuário, ou seja, dado do cliente dele indo para o GA4. O que
  // interessa para medir adoção é o escopo (pasta de time converte melhor que
  // pasta pessoal?) e o volume, não o rótulo.
  trackFolderCreated(shared: boolean): void {
    trackEvent('folder_created', { folder_scope: shared ? 'organization' : 'personal' });
  },

  trackProjectMovedToFolder(): void {
    trackEvent('project_moved_to_folder');
  },

  trackProjectImported(): void {
    trackEvent('project_imported');
    trackEvent('project_created', { creation_method: 'import' });
  },

  trackProjectDuplicated(): void {
    trackEvent('project_duplicated');
  },

  trackProjectLimitHit(currentPlan: string): void {
    trackEvent('project_limit_hit', { current_plan: currentPlan });
  },

  // ==================== MÓDULOS ====================
  trackModuleAccessed(moduleName: string): void {
    trackEvent('module_accessed', { module_name: moduleName });
  },

  trackFeatureGateLocked(featureName: string, currentPlan: string): void {
    trackEvent('feature_gate_locked', {
      feature_name: featureName,
      current_plan: currentPlan,
    });
  },

  // ==================== EXPORTAÇÃO ====================
  trackExport(format: string): void {
    trackEvent('export_completed', { format });
  },

  /**
   * Só existia `export_completed`, então a taxa de falha da exportação era invisível: o
   * funil mostrava sucesso e silêncio, e silêncio pode ser tanto "ninguém tentou" quanto
   * "todo mundo tentou e quebrou". `reason` vem do ExportFailureReason.
   */
  trackExportFailed(format: string, reason: string, module?: string): void {
    trackEvent('export_failed', { format, reason, module: module || 'unknown' });
  },

  trackExportStarted(format: string, module?: string): void {
    trackEvent('export_started', { format, module: module || 'unknown' });
  },

  // ==================== CONVITES ====================
  trackInviteAccepted(): void {
    trackEvent('invite_accepted');
  },

  // ==================== DEMO / LEAD ====================
  trackDemoRequest(role: string): void {
    trackEvent('demo_request', { role });
  },

  // ==================== ONBOARDING / ATIVAÇÃO ====================
  trackOnboardingWelcome(role: string, projectType: string): void {
    trackEvent('onboarding_welcome', { role, project_type: projectType });
  },

  trackOnboardingStarted(source: string = 'auto'): void {
    trackEvent('onboarding_started', { source });
  },

  trackOnboardingStep(stepIndex: number, stepKey: string): void {
    trackEvent('onboarding_step', { step_index: stepIndex, step_key: stepKey });
  },

  trackOnboardingSkipped(stepIndex: number): void {
    trackEvent('onboarding_skipped', { step_index: stepIndex });
  },

  trackOnboardingFinished(): void {
    trackEvent('onboarding_finished');
  },

  // Marcos de ativação: editar uma tarefa (M1) e atribuir um responsável (M2).
  // São o gate de "usuário ativado" e a métrica número 1 do funil.
  trackActivationM1Edit(): void {
    trackEvent('activation_m1_edit_task');
  },

  trackActivationM2Assign(): void {
    trackEvent('activation_m2_assign_owner');
  },

  trackActivationComplete(): void {
    trackEvent('activation_complete');
  },

  // Checklist "Primeiros Passos": cada item concluído e o fechamento do checklist.
  // item_key é uma chave estável (ChecklistKey), não o rótulo em português — o
  // rótulo muda com a copy e quebraria a série histórica no GA4.
  trackChecklistItemDone(itemKey: string): void {
    trackEvent('onboarding_item_done', { item_key: itemKey });
  },

  trackChecklistComplete(itemsDone: number): void {
    trackEvent('onboarding_checklist_complete', { items_done: itemsDone });
  },

  // ==================== PRODUTO / ENGAJAMENTO ====================
  // Ações do usuário DENTRO dos módulos (a navegação entre módulos já é coberta
  // por trackModuleAccessed). Foco em eventos de alto valor e baixa frequência,
  // sem PII e com parâmetros mínimos, para não estourar cardinalidade no GA4.

  // -- Tarefas (Gantt / Cronograma / Kanban / Tabela) --
  trackTaskCreated(source: string = 'manual'): void {
    trackEvent('task_created', { source });
  },
  trackTaskDeleted(): void {
    trackEvent('task_deleted');
  },
  /**
   * `status` recebe a CATEGORIA canônica da coluna (not_started, in_progress,
   * on_hold, completed), não o id dela. Desde que a coluna do Kanban virou
   * livre, o id é texto que cada cliente inventa: mandá-lo como dimensão
   * principal estouraria a cardinalidade do GA4 e o relatório deixaria de
   * agregar. O id vai em `column_id`, para drill quando fizer falta.
   */
  trackTaskStatusChanged(status: string, source: string = 'unknown', columnId?: string): void {
    trackEvent('task_status_changed', { status, source, ...(columnId ? { column_id: columnId } : {}) });
  },

  // -- Gantt / PMBOK --
  trackDependencyCreated(): void {
    trackEvent('task_dependency_created');
  },
  trackBaselineSet(): void {
    trackEvent('baseline_set');
  },
  trackCriticalPathViewed(): void {
    trackEvent('critical_path_viewed');
  },

  // -- Riscos --
  trackRiskCreated(): void {
    trackEvent('risk_created');
  },
  trackRiskUpdated(): void {
    trackEvent('risk_updated');
  },
  trackRiskDeleted(): void {
    trackEvent('risk_deleted');
  },

  // -- Custos --
  trackCostCreated(): void {
    trackEvent('cost_created');
  },

  // -- Pendências --
  trackPendenciaCreated(): void {
    trackEvent('pendencia_created');
  },

  // -- Mudanças / RACI --
  trackChangeRequestCreated(): void {
    trackEvent('change_request_created');
  },
  trackRaciChanged(): void {
    trackEvent('raci_assignment_changed');
  },

  // -- Equipe / Alocação --
  trackMemberAdded(): void {
    trackEvent('member_added');
  },
  trackAllocationChanged(): void {
    trackEvent('allocation_changed');
  },

  // -- Colaboração --
  trackCommentCreated(): void {
    trackEvent('comment_created');
  },
  trackProjectShared(): void {
    trackEvent('project_shared');
  },
  // Link interno de tarefa copiado no card. Vale como sinal de colaboração: só
  // copia quem tem alguém para mandar, e quem recebe precisa de conta para abrir.
  trackTaskLinkCopied(): void {
    trackEvent('task_link_copied');
  },
  /**
   * Instalacao do PWA. Mede o funil de instalado contra so-navegador, que e o
   * que decide se vale investir mais no app: no iPhone o push SO funciona com o
   * app instalado, entao esta taxa e o teto do alcance de notificacao.
   */
  trackPwaInstallPrompt(outcome: 'accepted' | 'dismissed'): void {
    trackEvent('pwa_install_prompt', { outcome });
  },
  trackWikiArticleViewed(articleId: string): void {
    trackEvent('wiki_article_viewed', { article_id: articleId });
  },

  // ==================== RECEITA / CHURN / TRIAL ====================
  // Trial interno (14 dias Pro no signup) expirou sem virar pago — sinal #1 de
  // churn pré-conversão. Disparado uma vez só (guard no SubscriptionContext).
  trackTrialExpired(daysInTrial: number): void {
    trackEvent('trial_expired', { days_in_trial: daysInTrial });
  },
  trackTrialWarning(daysRemaining: number): void {
    trackEvent('trial_warning', { days_remaining: daysRemaining });
  },
  // Checkout iniciado mas abandonado (?checkout=canceled). plan/billing_cycle
  // vêm do localStorage gravado em trackCheckoutInitiated.
  trackCheckoutAbandoned(plan: string, billingCycle: string): void {
    trackEvent('checkout_abandoned', { plan, billing_cycle: billingCycle });
  },

  // ==================== AQUISIÇÃO / FRICÇÃO ====================
  trackCtaClick(section: string, label: string): void {
    trackEvent('cta_click', { cta_section: section, cta_label: label });
  },
  trackSearch(term: string, resultsCount: number): void {
    trackEvent('search', { search_term: term.slice(0, 100), results_count: resultsCount });
  },
  trackAuthError(errorCode: string, mode: string): void {
    trackEvent('auth_error', { error_code: errorCode, mode, ...webviewParams() });
  },
  // Início do funil de OAuth (clique em "Entrar com Google"). A conclusão é o
  // sign_up/login com method=provider; start - conclusão = drop de OAuth.
  trackOAuthStart(provider: string): void {
    trackEvent('auth_oauth_start', { provider, ...webviewParams() });
  },
  trackCheckoutError(errorCode: string, plan: string): void {
    trackEvent('checkout_error', { error_code: errorCode, plan });
  },

  // ==================== COLABORAÇÃO / RETENÇÃO ====================
  // Convite enviado — fecha o funil enviado→aceito (invite_accepted).
  trackInviteSent(role: string, isNewUser: boolean): void {
    trackEvent('invite_sent', { role, is_new_user: isNewUser });
  },
  // Início de sessão autenticada (1x por sessão) — enriquece DAU/MAU com coorte.
  trackSessionStart(method: string = 'unknown'): void {
    trackEvent('session_start', { method });
  },
  trackProjectOpened(projectId: string): void {
    trackEvent('project_opened', { project_id: projectId });
  },

  // ==================== USER ID / PROPERTIES ====================
  setUser(userId: string, properties: Record<string, unknown> = {}): void {
    if (!isTrackingEnabled()) return;
    lastUser = { userId, properties }; // reaplicado no init se o gtag ainda não existir
    if (typeof window.gtag !== 'function') return;
    window.gtag('set', 'user_id', userId);
    window.gtag('set', 'user_properties', properties);
  },

  clearUser(): void {
    lastUser = null;
    if (!isTrackingEnabled() || typeof window.gtag !== 'function') return;
    window.gtag('set', 'user_id', null);
    window.gtag('set', 'user_properties', { plan_name: null });
  },
};

export default analytics;
